import {
  type CampoEscalar,
  type CampoGrupo,
  type ContextoDeRespuesta,
  type FilaDeGrupo,
  type PreguntaDeFila,
  type Respuesta,
  type RespuestaEscalar,
  respuestaEsValida,
} from '@pasos/schemas';
import { useId, useRef, useState } from 'react';
import { Boton } from '../../components/ui/Boton.js';
import { PACIENTE } from '../../copy/index.js';
import type { CatalogosDescargados } from '../catalogos.js';
import { Catalogo } from './Catalogo.js';
import { Fecha } from './Fecha.js';
import { Numero } from './Numero.js';
import { Opciones } from './Opciones.js';

type GrupoProps = {
  campo: CampoGrupo;
  valor: readonly FilaDeGrupo[] | undefined;
  onElegir: (valor: Respuesta | undefined) => void;
  contexto: ContextoDeRespuesta;
  catalogos: CatalogosDescargados;
};

type Fila = { clave: number; respuestas: Partial<Record<string, RespuestaEscalar>> };

function filasIniciales(campo: CampoGrupo, valor: readonly FilaDeGrupo[] | undefined): Fila[] {
  const cargadas = (valor ?? []).map((respuestas, indice) => ({ clave: indice, respuestas }));
  if (cargadas.length > 0) return cargadas;
  // Una fila vacía para empezar, salvo que el grupo admita cero filas.
  return Array.from(
    { length: Math.max(campo.minimo, campo.minimo === 0 ? 0 : 1) },
    (_, indice) => ({
      clave: indice,
      respuestas: {},
    }),
  );
}

/**
 * Un conjunto de preguntas que se repite (E3.25): la lista de medicamentos,
 * tres preguntas por fila.
 *
 * Cada fila es un `fieldset` con su nombre ("Medicamento 2") y su botón de
 * quitar, y al final "Agregar otro" hasta el máximo. Las preguntas de la fila
 * usan las mismas piezas que una pregunta suelta. La respuesta sale recién
 * cuando **todas las filas están completas** y la cantidad está en el rango: una
 * droga sin su potencia no es un dato, y el botón de seguir no se habilita hasta
 * que cada fila lo sea.
 */
export function Grupo({ campo, valor, onElegir, contexto, catalogos }: GrupoProps) {
  const [filas, setFilas] = useState<Fila[]>(() => filasIniciales(campo, valor));
  const siguienteClave = useRef(filas.length);

  function actualizar(nuevas: Fila[]) {
    setFilas(nuevas);
    const respuesta = nuevas.map((fila) => fila.respuestas as FilaDeGrupo);
    onElegir(respuestaEsValida(campo, respuesta, contexto) ? respuesta : undefined);
  }

  function responder(clave: number, pregunta: string, valorDeLaFila: Respuesta | undefined) {
    actualizar(
      filas.map((fila) => {
        if (fila.clave !== clave) return fila;
        const respuestas = { ...fila.respuestas };
        if (valorDeLaFila === undefined || Array.isArray(valorDeLaFila))
          delete respuestas[pregunta];
        else respuestas[pregunta] = valorDeLaFila;
        return { ...fila, respuestas };
      }),
    );
  }

  function agregar() {
    const clave = siguienteClave.current;
    siguienteClave.current += 1;
    actualizar([...filas, { clave, respuestas: {} }]);
  }

  return (
    <div className="flex flex-col gap-5">
      {filas.map((fila, indice) => (
        <fieldset
          className="m-0 flex flex-col gap-5 rounded-lg bg-muted p-4 shadow-low ring-1 ring-border-soft"
          key={fila.clave}
        >
          <legend className="sr-only">
            {PACIENTE.formulario.fila(campo.etiquetaDeFila, indice + 1)}
          </legend>
          <div className="flex items-center justify-between gap-3">
            <p aria-hidden="true" className="m-0 text-xl font-bold text-heading">
              {PACIENTE.formulario.fila(campo.etiquetaDeFila, indice + 1)}
            </p>
            {filas.length > campo.minimo ? (
              <Boton
                onClick={() => actualizar(filas.filter((otra) => otra.clave !== fila.clave))}
                variante="texto"
              >
                {PACIENTE.formulario.quitar(campo.etiquetaDeFila, indice + 1)}
              </Boton>
            ) : null}
          </div>
          {campo.preguntas.map((pregunta) => (
            <PreguntaDeLaFila
              catalogos={catalogos}
              contexto={contexto}
              key={pregunta.id}
              onElegir={(nuevo) => responder(fila.clave, pregunta.id, nuevo)}
              pregunta={pregunta}
              valor={fila.respuestas[pregunta.id]}
            />
          ))}
        </fieldset>
      ))}

      {filas.length < campo.maximo ? (
        <Boton onClick={agregar} variante="secundario">
          {PACIENTE.formulario.agregarOtro}
        </Boton>
      ) : null}
      <p className="m-0 text-base text-subtle">{PACIENTE.formulario.completaLaFila}</p>
    </div>
  );
}

function PreguntaDeLaFila({
  pregunta,
  valor,
  onElegir,
  contexto,
  catalogos,
}: {
  pregunta: PreguntaDeFila;
  valor: RespuestaEscalar | undefined;
  onElegir: (valor: Respuesta | undefined) => void;
  contexto: ContextoDeRespuesta;
  catalogos: CatalogosDescargados;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-3">
      <h3 className="m-0 text-xl leading-tight font-bold text-heading" id={id}>
        {pregunta.texto}
      </h3>
      {pregunta.ayuda ? <p className="m-0 -mt-2 text-lg text-subtle">{pregunta.ayuda}</p> : null}
      <ControlEscalar
        campo={pregunta.campo}
        catalogos={catalogos}
        contexto={contexto}
        etiquetadoPor={id}
        onElegir={onElegir}
        valor={valor}
      />
    </div>
  );
}

/** La pieza de cada tipo escalar. La comparten una pregunta suelta y una de fila. */
export function ControlEscalar({
  campo,
  valor,
  onElegir,
  etiquetadoPor,
  contexto,
  catalogos,
}: {
  campo: CampoEscalar;
  valor: RespuestaEscalar | undefined;
  onElegir: (valor: Respuesta | undefined) => void;
  etiquetadoPor: string;
  contexto: ContextoDeRespuesta;
  catalogos: CatalogosDescargados;
}) {
  switch (campo.tipo) {
    case 'numero':
      return (
        <Numero
          campo={campo}
          etiquetadoPor={etiquetadoPor}
          onElegir={onElegir}
          valor={typeof valor === 'number' ? valor : undefined}
        />
      );
    case 'fecha':
      return (
        <Fecha
          campo={campo}
          contexto={contexto}
          etiquetadoPor={etiquetadoPor}
          onElegir={onElegir}
          valor={typeof valor === 'string' ? valor : undefined}
        />
      );
    case 'catalogo':
      return (
        <Catalogo
          campo={campo}
          entradas={catalogos[campo.catalogo] ?? []}
          etiquetadoPor={etiquetadoPor}
          onElegir={onElegir}
          valor={typeof valor === 'string' ? valor : undefined}
        />
      );
    default:
      return (
        <Opciones campo={campo} etiquetadoPor={etiquetadoPor} onElegir={onElegir} valor={valor} />
      );
  }
}
